import * as THREE from 'three/webgpu';
import {isSketchGroup} from '../../guards';
import type {SketchGroup} from '../SketchGroup';

/**
 * 文件定位：
 * 1. 当前文件位于 `cad-core/mesh/sketch/utils` 工具目录；
 * 2. 负责在草图几何对象发生定义级变更时，向其所属 `SketchGroup` 上报 dirty；
 * 3. 该工具用于把“几何写入 -> 草图定义脏标记”这条链路统一收口到底层几何层。
 */

/**
 * 向目标对象所属的草图组上报 dirty。
 *
 * 设计说明：
 * 1. 草图点、草图边、轮廓控制点都可能从不同层级写入几何；
 * 2. dirty 上报不能散落在拖拽策略、命令处理器或 UI 适配层；
 * 3. 因此这里统一沿父链查找 `SketchGroup`，把“定义变更”语义固定在几何对象层。
 *
 * @param owner 起始对象。
 * @param input dirty 上报上下文。
 * @returns 已完成 dirty 上报的所属草图组；未命中时返回 null。
 */
export function markOwnerSketchGroupDirty(
    owner: THREE.Object3D,
    input: {
        reason: string;
        sourceObjectId?: string;
    },
): SketchGroup | null {
    let current: THREE.Object3D | null = owner;
    while (current) {
        if (isSketchGroup(current)) {
            // `hasUncommittedDefinitionChanges()` 只能用于提交阶段判断，首次几何写入前必然为 false，
            // 因此 dirty 入口必须无条件递增定义版本，避免首个变更被错误吞掉。
            current.markDefinitionDirty({
                reason: input.reason,
                sourceObjectId: input.sourceObjectId,
            });
            return current;
        }
        current = current.parent;
    }

    return null;
}
